import React from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Container,
  CssBaseline,
  AppBar,
  Toolbar,
  Paper,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { Player } from '@lottiefiles/react-lottie-player';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import LoginForm from '../components/auth/LoginForm';

// Create a professional theme using deep blues and a modern font
const loginTheme = createTheme({
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica Neue", "Arial", sans-serif',
    h3: {
      fontWeight: 800,
      letterSpacing: '-0.5px',
    },
    h4: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    }
  },
  palette: {
    primary: {
      main: '#0d47a1', // Deep corporate blue
      light: '#5472d3',
      dark: '#002171',
    },
    secondary: {
      main: '#1976d2', // Brighter blue for accents
    },
    background: {
      default: '#f8fafd', // Very light blue/grey background
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          paddingTop: 10,
          paddingBottom: 10,
        },
      },
    },
  },
});

const LoginPage = () => {
  const navigate = useNavigate();
  const logoUrl = `${process.env.PUBLIC_URL}/images/logo.png`;

  const handleSignUpClick = () => {
    navigate('/register');
  };

  // Project-specific features with paths to Lottie files
  const features = [
    { text: 'Smart POS & Offline Billing', lottie: '/lotties/smart-billing.lottie' },
    { text: 'Comprehensive Inventory Control', lottie: '/lotties/comprehensive-inventory.lottie' },
    { text: 'GST Ready Invoicing & Reports', lottie: '/lotties/gst-ready.lottie' },
    { text: 'Real-time Business Analytics', lottie: '/lotties/realtime-business.lottie' },
    { text: 'Multi-Location Management', lottie: '/lotties/multi-location.lottie' },
    { text: 'Integrated B2B/B2C Ecommerce', lottie: '/lotties/b2b-b2c.lottie' },
  ];

  return (
    <ThemeProvider theme={loginTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>

        {/* Top Bar */}
        <AppBar position="static" color="transparent" elevation={0} sx={{ borderBottom: '1px solid rgba(0,0,0,0.05)', bgcolor: 'white' }}>
          <Toolbar sx={{ justifyContent: 'space-between', py: 1, px: { xs: 2, md: 6 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', mr: 1.5 }}>
                <img
                  src={logoUrl}
                  alt="Tiya POS Logo"
                  style={{ height: '40px', width: '40px', objectFit: 'contain' }}
                />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main', fontSize: '1.5rem' }}>
                Tiya POS
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, alignItems: 'center' }}>
              <Button color="inherit" onClick={handleSignUpClick} sx={{ color: 'primary.main', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Sign Up
              </Button>
              <Button variant="contained" color="primary" sx={{ px: { xs: 2, md: 3 }, boxShadow: '0 4px 12px rgba(13, 71, 161, 0.2)' }}>
                Download App
              </Button>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden' }}>

          {/* Professional Background Elements */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: { xs: '100%', md: '55%' },
            background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
            zIndex: 0,
            clipPath: { xs: 'none', md: 'polygon(15% 0, 100% 0, 100% 100%, 0% 100%)' },
            opacity: { xs: 0.35, md: 1 },
          }} />

          <Box sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(25,118,210,0.1) 0%, rgba(25,118,210,0) 70%)',
            zIndex: 0,
          }} />

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 4, md: 8 } }}>
            <Grid container spacing={{ xs: 4, md: 6 }} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Box sx={{ pr: { md: 4 } }}>
                  <Typography variant="h3" component="h1" gutterBottom sx={{ color: '#0a192f', lineHeight: 1.2, mb: 2, fontSize: { xs: '2.5rem', md: '3.2rem' } }}>
                    Advanced Cloud POS & Inventory Management
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#455a64', mb: 5, fontWeight: 400, lineHeight: 1.6 }}>
                    Streamline your retail and wholesale business with our all-in-one platform designed for growth and efficiency.
                  </Typography>

                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    {features.map((feature, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            bgcolor: 'white',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                          }}>
                            {feature.lottie.endsWith('.lottie') ? (
                              <DotLottieReact
                                src={feature.lottie}
                                loop
                                autoplay
                                style={{ height: '32px', width: '32px' }}
                              />
                            ) : (
                              <Player
                                autoplay
                                loop
                                src={feature.lottie}
                                style={{ height: '32px', width: '32px' }}
                                fallback={<Box sx={{ width: 24, height: 24, bgcolor: '#e0e0e0', borderRadius: '50%' }} />}
                              />
                            )}
                          </Box>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#263238', lineHeight: 1.3 }}>
                            {feature.text}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="large"
                      onClick={handleSignUpClick}
                      sx={{ px: 4, py: 1.5, fontSize: '1.1rem', borderWidth: '2px', bgcolor: 'white', '&:hover': { borderWidth: '2px', bgcolor: '#f5f5f5' } }}
                    >
                      Start Free Trial
                    </Button>
                  </Box>
                </Box>
              </Grid>

              {/* Login card — replaces hero Lottie */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: { xs: 'center', md: 'flex-end' },
                    alignItems: { xs: 'stretch', md: 'center' },
                    minHeight: { xs: 'auto', md: 520 },
                    height: '100%',
                  }}
                >
                  <Paper
                    elevation={12}
                    sx={{
                      width: '100%',
                      maxWidth: 440,
                      p: { xs: 3, sm: 4, md: 5 },
                      borderRadius: 3,
                      background: 'rgba(255, 255, 255, 0.98)',
                      border: '1px solid rgba(13, 71, 161, 0.08)',
                      boxShadow: '0 12px 40px rgba(13, 71, 161, 0.12), 0 4px 12px rgba(0,0,0,0.06)',
                      transform: { md: 'translateX(12px)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <img src={logoUrl} alt="" style={{ height: 52, width: 52, objectFit: 'contain' }} />
                    </Box>
                    <LoginForm />
                    <Box sx={{ mt: 3 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        size="large"
                        onClick={handleSignUpClick}
                        sx={{
                          py: 1.5,
                          fontSize: '1rem',
                          borderWidth: '2px',
                          fontWeight: 600,
                          textTransform: 'none',
                          bgcolor: 'white',
                          '&:hover': { borderWidth: '2px', bgcolor: '#f5f5f5' },
                        }}
                      >
                        Start your free trial
                      </Button>
                    </Box>
                  </Paper>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default LoginPage;
